import { input, type SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  Input,
  output,
} from '@angular/core';
import type { ControlValueAccessor } from '@angular/forms';
import { NG_VALUE_ACCESSOR } from '@angular/forms';

import moment from 'moment-timezone';

import { TranslatePipe } from '@core/i18n-legacy';
import { resolveWeekType } from '@core/time-legacy';
import type { DatePickerDirection } from '@core/time-ui';
import type { IconName } from '@core/user-interface-legacy';
import {
  ConnectedOverlayDirective,
  OverlayPosition,
  ButtonComponent,
  GroupComponent,
  IconComponent,
  OverlayOriginDirective,
} from '@core/user-interface-legacy';

import { ScheduleDateDropdownComponent } from './schedule-date-dropdown/schedule-date-dropdown.component';

@Component({
  selector: 'rt-schedule-date-picker',
  templateUrl: 'schedule-date-picker.component.html',
  styleUrls: ['./schedule-date-picker.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.disable-invalid-styles]': 'disableInvalidStyles()',
    '(click)': 'onTouched?.()',
  },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      // eslint-disable-next-line @angular-eslint/no-forward-ref
      useExisting: forwardRef(() => ScheduleDatePickerComponent),
    },
  ],
  imports: [
    GroupComponent,
    OverlayOriginDirective,
    ButtonComponent,
    IconComponent,
    ConnectedOverlayDirective,
    ScheduleDateDropdownComponent,
    TranslatePipe,
  ],
})
export class ScheduleDatePickerComponent implements ControlValueAccessor {
  private static readonly DATE_FORMAT: string = 'DD MMM YYYY';

  @Input() public showWeekRange: boolean = false;
  @Input() public weekView: boolean = true;
  @Input() public closeOnDateChange: boolean = false;
  @Input() public direction: DatePickerDirection = 'today-future';
  @Input() public fullWidth: boolean = false;
  @Input() public showClear?: boolean = false;
  @Input() public useYearNav: boolean = false;
  @Input() public leftIcon?: IconName;
  @Input() public rightIcon?: IconName;
  @Input() public blackoutDates: ((date: moment.Moment) => boolean) | null = null;
  @Input() public placeholder?: string;
  @Input() public disabled: boolean = false;
  public disableInvalidStyles = input(false);

  @Input() public date: string | moment.Moment;
  @Input() public weekOffset: number;
  @Input() public weekDays: number = 5;

  public dateChange = output<moment.Moment>();

  public datePickerPositions: OverlayPosition[] = [
    OverlayPosition.BottomLeft,
    OverlayPosition.TopLeft,
  ];
  public currentDate?: string;

  protected onTouched: (() => void) | null = null;

  constructor(private cdRef: ChangeDetectorRef) {}

  public writeValue(value: string): void {
    this.setDate(value);
    this.cdRef.detectChanges();
  }
  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public setDisabledState(isDisabled: boolean): void {}

  public ngOnInit(): void {
    this.setDate(this.date);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const { date } = changes;

    this.setDate(date && date.currentValue);
  }

  public toggleDropdown(dropdown: ConnectedOverlayDirective): void {
    dropdown.open = !dropdown.open;
    this.cdRef.detectChanges();
  }

  public getFormattedInterval(): string {
    const sundayOffset = resolveWeekType() === 'week' && this.weekDays === 5 ? 1 : 0;
    const date = moment().startOf(resolveWeekType()).add(this.weekOffset, 'w');

    return (
      date.add(sundayOffset, 'd').format('DD - ') + date.add(this.weekDays - 1, 'd').format('DD')
    );
  }

  public getFormattedMonthAndYear(): string {
    const date = this.date !== '' ? moment(this.date) : moment();

    return date.format('MMM YYYY');
  }

  public getFormattedDay(): string {
    const date = this.date !== '' ? moment(this.date) : moment();

    return date.format('DD');
  }

  public getFormattedDate(): string {
    const date = moment().startOf(resolveWeekType()).add(this.weekOffset, 'w');
    const startDate = date.format('MMM YYYY');
    const endDate = date.clone().add(this.weekDays, 'd').format('MMM YYYY');

    return startDate !== endDate ? date.format('MMM/') + endDate : startDate;
  }

  public onDateChange(value: moment.Moment | undefined, dropdown: ConnectedOverlayDirective): void {
    const date = value?.format() ?? '';
    this.setDate(date);

    dropdown.open = false;
    this.cdRef.detectChanges();

    this.dateChange.emit(value!);
    this.onChange(date);
  }

  private setDate(date: string | moment.Moment): void {
    if (!date || !moment(date).isValid()) {
      this.currentDate = undefined;
      return;
    }

    this.currentDate = moment(date).format(ScheduleDatePickerComponent.DATE_FORMAT);
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onChange: any = () => {};
}
